import type { ComponentMeta } from "@mijalowcode/types";
import { ComponentCategory, PropType } from "@mijalowcode/types";

export const StatisticMeta: ComponentMeta = {
  componentName: "Statistic",
  package: "@mijadesign/web",
  title: "统计数值",
  description: "展示统计数据",
  category: ComponentCategory.DATA_DISPLAY,
  icon: "NumberOutlined",
  canHaveChildren: false,
  props: [
    {
      name: "title",
      label: "标题",
      type: PropType.STRING,
      group: "基础",
    },
    {
      name: "value",
      label: "数值",
      type: PropType.NUMBER,
      group: "基础",
    },
    {
      name: "prefix",
      label: "前缀",
      type: PropType.STRING,
      group: "装饰",
    },
    {
      name: "suffix",
      label: "后缀",
      type: PropType.STRING,
      group: "装饰",
    },
    {
      name: "precision",
      label: "小数位数",
      type: PropType.NUMBER,
      group: "样式",
    },
  ],
  defaultProps: {
    title: "统计数值",
    value: 0,
  },
  npm: {
    package: "@mijadesign/web",
    exportName: "Statistic",
  },
  snippet: {
    componentName: "Statistic",
    package: "@mijadesign/web",
    props: { title: "统计数值", value: 0 },
  },
};

export const TimelineMeta: ComponentMeta = {
  componentName: "Timeline",
  package: "@mijadesign/web",
  title: "时间轴",
  description: "展示时间进程",
  category: ComponentCategory.DATA_DISPLAY,
  icon: "ClockCircleOutlined",
  canHaveChildren: true,
  props: [
    {
      name: "mode",
      label: "模式",
      type: PropType.ENUM,
      options: [
        { label: "默认", value: "left" },
        { label: "交替", value: "alternate" },
      ],
      group: "样式",
    },
  ],
  npm: {
    package: "@mijadesign/web",
    exportName: "Timeline",
  },
  snippet: {
    componentName: "Timeline",
    package: "@mijadesign/web",
    props: {},
  },
};

export const TimelineItemMeta: ComponentMeta = {
  componentName: "Timeline.Item",
  package: "@mijadesign/web",
  title: "时间轴项",
  description: "时间轴的单个节点",
  category: ComponentCategory.DATA_DISPLAY,
  icon: "CircleOutlined",
  canHaveChildren: true,
  props: [
    {
      name: "color",
      label: "颜色",
      type: PropType.ENUM,
      defaultValue: "blue",
      options: [
        { label: "蓝色", value: "blue" },
        { label: "红色", value: "red" },
        { label: "绿色", value: "green" },
        { label: "灰色", value: "gray" },
      ],
      group: "样式",
    },
  ],
  defaultProps: {
    color: "blue",
  },
  npm: {
    package: "@mijadesign/web",
    exportName: "Timeline",
  },
  snippet: {
    componentName: "Timeline.Item",
    package: "@mijadesign/web",
    props: {},
  },
};

export const ResultMeta: ComponentMeta = {
  componentName: "Result",
  package: "@mijadesign/web",
  title: "结果页",
  description: "展示处理结果",
  category: ComponentCategory.FEEDBACK,
  icon: "CheckCircleOutlined",
  canHaveChildren: false,
  props: [
    {
      name: "status",
      label: "状态",
      type: PropType.ENUM,
      defaultValue: "info",
      options: [
        { label: "成功", value: "success" },
        { label: "信息", value: "info" },
        { label: "警告", value: "warning" },
        { label: "错误", value: "error" },
        { label: "403", value: "403" },
        { label: "404", value: "404" },
        { label: "500", value: "500" },
      ],
      group: "基础",
    },
    {
      name: "title",
      label: "标题",
      type: PropType.STRING,
      group: "基础",
    },
    {
      name: "subTitle",
      label: "副标题",
      type: PropType.STRING,
      group: "基础",
    },
  ],
  defaultProps: {
    status: "info",
    title: "操作成功",
  },
  npm: {
    package: "@mijadesign/web",
    exportName: "Result",
  },
  snippet: {
    componentName: "Result",
    package: "@mijadesign/web",
    props: { status: "success", title: "操作成功" },
  },
};

export const TooltipMeta: ComponentMeta = {
  componentName: "Tooltip",
  package: "@mijadesign/web",
  title: "文字提示",
  description: "简单的文字提示",
  category: ComponentCategory.FEEDBACK,
  icon: "QuestionCircleOutlined",
  canHaveChildren: true,
  props: [
    {
      name: "title",
      label: "提示内容",
      type: PropType.STRING,
      group: "基础",
    },
  ],
  defaultProps: {
    title: "提示文字",
  },
  npm: {
    package: "@mijadesign/web",
    exportName: "Tooltip",
  },
  snippet: {
    componentName: "Tooltip",
    package: "@mijadesign/web",
    props: { title: "提示文字" },
  },
};

export const PopconfirmMeta: ComponentMeta = {
  componentName: "Popconfirm",
  package: "@mijadesign/web",
  title: "气泡确认框",
  description: "带有确认和取消按钮的弹出提示",
  category: ComponentCategory.FEEDBACK,
  icon: "QuestionCircleOutlined",
  canHaveChildren: true,
  props: [
    {
      name: "title",
      label: "确认标题",
      type: PropType.STRING,
      group: "基础",
    },
    {
      name: "okText",
      label: "确认文字",
      type: PropType.STRING,
      defaultValue: "确定",
      group: "文本",
    },
    {
      name: "cancelText",
      label: "取消文字",
      type: PropType.STRING,
      defaultValue: "取消",
      group: "文本",
    },
  ],
  defaultProps: {
    title: "确认删除？",
    okText: "确定",
    cancelText: "取消",
  },
  npm: {
    package: "@mijadesign/web",
    exportName: "Popconfirm",
  },
  snippet: {
    componentName: "Popconfirm",
    package: "@mijadesign/web",
    props: { title: "确认删除？" },
  },
};

export const PopoverMeta: ComponentMeta = {
  componentName: "Popover",
  package: "antd",
  title: "气泡卡片",
  description: "弹出卡片式的容器",
  category: ComponentCategory.FEEDBACK,
  icon: "MessageOutlined",
  canHaveChildren: true,
  props: [
    {
      name: "title",
      label: "标题",
      type: PropType.STRING,
      group: "基础",
    },
  ],
  npm: {
    package: "antd",
    exportName: "Popover",
  },
  snippet: {
    componentName: "Popover",
    package: "antd",
    props: { title: "标题" },
  },
};

export const SkeletonMeta: ComponentMeta = {
  componentName: "Skeleton",
  package: "@mijadesign/web",
  title: "骨架屏",
  description: "内容加载时的占位骨架",
  category: ComponentCategory.FEEDBACK,
  icon: "ScissorOutlined",
  canHaveChildren: false,
  props: [
    {
      name: "active",
      label: "动画",
      type: PropType.BOOLEAN,
      defaultValue: true,
      group: "样式",
    },
    {
      name: "avatar",
      label: "显示头像",
      type: PropType.BOOLEAN,
      defaultValue: false,
      group: "内容",
    },
    {
      name: "title",
      label: "显示标题",
      type: PropType.BOOLEAN,
      defaultValue: true,
      group: "内容",
    },
    {
      name: "paragraph",
      label: "显示段落",
      type: PropType.BOOLEAN,
      defaultValue: true,
      group: "内容",
    },
  ],
  defaultProps: {
    active: true,
    title: true,
    paragraph: true,
  },
  npm: {
    package: "@mijadesign/web",
    exportName: "Skeleton",
  },
  snippet: {
    componentName: "Skeleton",
    package: "@mijadesign/web",
    props: {},
  },
};
